import {
  Button,
  Center,
  Flex,
  Group,
  LoadingOverlay,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import TextEditor from "../../../../components/ExternalEditors/TextEditor";
import { useEffect, useMemo, useRef, useState } from "react";
import { useConfigStore } from "../../../../stores/ConfigStore";
import { getFields } from "../../../../api/ContactAPI";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  IconDeviceFloppy,
  IconTemplate,
  IconTextSize,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import EmailModalPreview from "../../../../components/View/EmailModalPreview";
import TemplateEditor from "../../../../components/ExternalEditors/TemplateEditor";
import BackButton from "../../../../components/View/BackButton";
import useNavigationBlocker from "../../../../components/View/NavigationBlocker";
import {
  editSignature,
  findSignature,
  getSignatures,
} from "../../../../api/ConfigsApi";
import FilesModal from "../../files/FilesModal";

const EditSignature = ({ handleClose, onEdited, model }) => {
  const editorRef = useRef();
  const templateEditorRef = useRef();
  const lang = useLanguage((s) => s.language);
  const datafields = useConfigStore((s) => s.datafields);
  const [loading, setLoading] = useState(model?.html ? false : true);
  const [dirty, setDirty] = useState(false);
  const datafieldTokens = useMemo(() => {
    let df = datafields.map((x) => ({
      label: x.name,
      value: x.identifier_placeholder,
    }));
    df.push(
      {
        label:
          lang.components.variable_manager.dialog.unsubscribe_variable_name,
        value: "%unsubscribe%",
        name: lang.components.variable_manager.dialog.unsubscribe_variable_name,
      },
      {
        label: lang.components.variable_manager.dialog.webversion_variable_name,
        value: "%webversion%",
        name: lang.components.variable_manager.dialog.webversion_variable_name,
      }
    );
    return df;
  }, [datafields, lang]);

  const datafieldTokensTemplate = useMemo(() => {
    let mergeTags = {
      [`[ ${lang.components.variable_manager.dialog.unsubscribe_variable_name} ]`]:
      {
        name: lang.components.variable_manager.dialog
          .unsubscribe_variable_name,
        value: "%unsubscribe%",
        sample: `[ ${lang.components.variable_manager.dialog.unsubscribe_variable_name} ]`,
      },
      [`[ ${lang.components.variable_manager.dialog.webversion_variable_name} ]`]:
      {
        name: lang.components.variable_manager.dialog
          .webversion_variable_name,
        value: "%webversion%",
        sample: `[ ${lang.components.variable_manager.dialog.webversion_variable_name} ]`,
      },
    };
    datafields.forEach((x) => {
      mergeTags = {
        ...mergeTags,
        [`[ ${x.name.toUpperCase()} ]`]: {
          name: x.name,
          value: x.identifier_placeholder,
          sample: `[ ${x.name.toUpperCase()} ]`,
        },
      };
    });
    return mergeTags;
  }, [datafields, lang]);

  const [modelProfile, setModelProfile] = useState(model);

  //#region Creation Type
  const [active, setActive] = useState(
    model?.is_template ? "template" : "text"
  );
  const [shouldWarn, setShouldWarn] = useState(true);

  const onActiveChange = (v) => {
    if (shouldWarn)
      modals.openConfirmModal({
        title: "Attention",
        children: (
          <Text size="sm">
            Vos modifications ne seront pas sauvegardés si vous changez
            d'éditeur.
          </Text>
        ),
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onCancel: () => { },
        onConfirm: () => {
          setShouldWarn(false);
          setActive(v);
        },
        centered: true,
      });
    else setActive(v);
  };

  const handleWarn = () => {
    setShouldWarn?.(true);
    setDirty(true);
  };
  //#endregion

  useEffect(() => {
    getFields();
    if (!model?.html) {
      setLoading(true);

      findSignature(model?.id)
        .then((response) => {
          setModelProfile(response.data.email_signature_profile);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [model]);

  const handlePreview = ({ setHtml, setSubject, open }) => {
    const fd = new FormData(document.getElementById("model-form"));
    const data = Object.fromEntries(fd);

    if (active === "text") {
      setHtml(editorRef.current.getContent());
      setSubject(data.subject);
      open();
    } else {
      templateEditorRef.current.exportHtml((ex) => {
        setHtml(ex.html);
        setSubject(data.subject);
        open();
      });
    }
  };

  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    let html = "";
    let text = "";
    let design_json = "";

    if (active === "text") {
      html = editorRef.current.getContent();
      text = editorRef.current.getContent({ format: "text" });
    } else {
      html = await new Promise((resolve) =>
        templateEditorRef.current.exportHtml((ex) => {
          resolve(ex.html);
        })
      );
      text = await new Promise((resolve) =>
        templateEditorRef.current.exportPlainText((ex) => {
          resolve(ex.text);
        })
      );
      design_json = await new Promise((resolve) =>
        templateEditorRef.current.saveDesign((design) => {
          resolve(JSON.stringify(design));
        })
      );
    }

    return editSignature({
      id: model?.id,
      name: data.name,
      html,
      text,
      template: design_json,
      is_template: active !== "text",
    })
      .then((response) => {
        setSubmitting(true);
        getSignatures()
          .then(() => {
            setDirty(false);
            notifications.show({
              title: "Signature modifiée",
              message: "Votre Signature a bien été modifiée",
              color: "tertiary.8",
            });
            setTimeout(() => {
              onEdited?.();
              handleClose?.();
            }, 20);
          })
          .finally(() => {
            setSubmitting(false);
          });
      })
      .catch(() => setSubmitting(false));
  };

  useNavigationBlocker(dirty, "submitter");

  return (
    <>
      {handleClose && (
        <LoadingOverlay
          visible={loading || submitting}
          overlayProps={{ blur: 1 }}
          zIndex={1001}
        />
      )}

      <Stack
        component={"form"}
        onSubmit={onSubmit}
        gap={0}
        id="model-form"
        flex={"1 1 auto"}
        w={"100%"}
        onChange={() => setDirty(true)}
      >
        <Group wrap="nowrap">
          <TextInput
            key={"name-" + modelProfile?.id}
            label={lang.model_emails.create.dialog.name}
            placeholder={lang.model_emails.create.dialog.name}
            defaultValue={modelProfile?.name}
            name="name"
            w="60%"
            required
            size="md"
            data-autofocus
            autoFocus
          />
        </Group>

        <SegmentedControl
          value={active}
          onChange={onActiveChange}
          data={[
            {
              value: "text",
              label: (
                <Center style={{ gap: 10 }}>
                  <IconTextSize />
                  <span>Editeur de Texte</span>
                </Center>
              ),
            },
            {
              value: "template",
              label: (
                <Center style={{ gap: 10 }}>
                  <IconTemplate />
                  <span>Editeur de Template</span>
                </Center>
              ),
              // disabled: true,
            },
          ]}
          size="sm"
          mt={"sm"}
          style={{
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
          }}
          mb={8}
        />

        {active === "text" ? (
          <TextEditor
            editorRef={editorRef}
            placeholder={lang.model_emails.create.dialog.placeholder}
            onKeyUp={handleWarn}
            tokens={datafieldTokens}
            onInit={(editor) => editor.setContent(modelProfile?.html)}
            longVariableButton
          >
            <Group
              wrap="nowrap"
              style={{
                position: "absolute",
                top: 9,
                left: 136,
                zIndex: 4,
              }}
              gap={"lg"}
            >
              <FilesModal editorRef={editorRef} isLongButton />
            </Group>
          </TextEditor>
        ) : (
          <TemplateEditor
            onReady={(editor) => {
              if (modelProfile?.json_template)
                editor.loadDesign(JSON.parse(modelProfile?.json_template));
              templateEditorRef.current = editor;
              templateEditorRef.current.addEventListener(
                "design:updated",
                function (data) {
                  setShouldWarn?.(true);
                  setDirty(true);
                }
              );
              templateEditorRef.current.setMergeTags(datafieldTokensTemplate);
            }}
          />
        )}

        <Group justify="space-between" mt={"sm"}>
          <Flex>
            <EmailModalPreview
              getData={handlePreview}
              noSign
              isSign
              disableEdit
              size="md"
            />
          </Flex>
          <Flex gap={"md"}>
            <BackButton onClick={handleClose} size="md" />
            <Button
              id="submitter"
              type="submit"
              size="md"
              loading={submitting}
              leftSection={<IconDeviceFloppy size={18} />}
            >
              {lang.model_emails.edit.dialog.submit_button}
            </Button>
          </Flex>
        </Group>
      </Stack>
    </>
  );
};

export default EditSignature;
